/* oxlint-disable typescript/no-unused-vars */
import { useMutation, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { showToast } from '@/components/Layout/Toast';

export interface TrpcToastConfig {
    /** Toast to show when `data.success === true` */
    successMessage?: string;
    /** Fallback if the server gives no error text at all */
    fallbackErrorMessage?: string;
}

/**
 * Wraps a mutationOptions()object with uniform toast handling.
 *
 * @param baseOpts – result of `trpc.foo.bar.mutationOptions()`
 * @param cfg      – per‑call toast texts
 */
export function useTrpcMutation<
    // ---------- generic parameters show up in the signatures below ----------
    TData extends { success: boolean; errorMessages?: string[] },
    TVars = void,
    TContext = unknown
>(
    // tRPC adds a few extra props (`throwOnError`, etc.); we drop the ones
    // React‑Query’s UseMutationOptions doesn’t know about.
    baseOpts: UseMutationOptions<TData, TRPCClientErrorLike<any>, TVars, TContext> & {
        throwOnError?: unknown;
    },
    cfg: TrpcToastConfig = {}
): UseMutationResult<TData, TRPCClientErrorLike<any>, TVars, TContext> {
    // Strip tRPC‑specific props that confuse React‑Query’s type expectations.
    // oxlint-disable-next-line no-unused-vars
    const { throwOnError, ...opts } = baseOpts;

    return useMutation<TData, TRPCClientErrorLike<any>, TVars, TContext>({
        ...opts,
        retry: false, // mutations shouldn't retry silently
        /* ------------------------------------------------------------- */
        /*                    SUCCESS (2 possible paths)                 */
        /* ------------------------------------------------------------- */
        onSuccess: (data, vars, onMutateResult, mctx) => {
            if (!data.success) {
                // ① Server signalled failure even though HTTP request succeeded.
                if (Array.isArray(data.errorMessages) && data.errorMessages.length) {
                    if (!data.errorMessages.includes('2FA code is required.')) {
                        data.errorMessages.forEach((m) => showToast(m, 'error'));
                    }
                } else {
                    showToast(cfg.fallbackErrorMessage ?? 'Operation failed', 'error');
                }
            } else if (cfg.successMessage) showToast(cfg.successMessage, 'success');

            // Delegate to caller‑supplied callback (if any)
            opts.onSuccess?.(data, vars, onMutateResult, mctx);
        },
        /* ------------------------------------------------------------- */
        /*                       NETWORK / CODE ERROR                    */
        /* ------------------------------------------------------------- */
        onError: (err, vars, onMutateResult, mctx) => {
            const msg =
                err?.message && typeof err.message === 'string' ? err.message : (cfg.fallbackErrorMessage ?? 'Something went wrong');

            showToast(msg, 'error');
            opts.onError?.(err, vars, onMutateResult, mctx);
        }
    });
}
